import type { pendingInviteDTOSchema } from '@tempo-io/id-dto';
import { Avatar, AvatarFallback } from '@tempo-io/sui-avatar';
import { Badge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { Card, CardContent } from '@tempo-io/sui-card';
import { BusinessCenter, CheckCircle, Email, Schedule } from '@tempo-io/sui-icons';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Invite = z.output<typeof pendingInviteDTOSchema>;

type InvitesListProps = {
  invites: Invite[];
  onAcceptInvite: (invite: Invite) => void | Promise<void>;
  onSkip?: () => void;
  skipButtonText?: string;
  isAcceptingInvite?: boolean;
};

export function InvitesList({
  invites,
  onAcceptInvite,
  onSkip,
  skipButtonText,
  isAcceptingInvite,
}: InvitesListProps) {
  const { t } = useTranslation();
  const [acceptingId, setAcceptingId] = useState<number | null>(null);
  const isEmpty = invites.length === 0;
  const handleAccept = useCallback(
    async (invite: Invite) => {
      setAcceptingId(invite.id);
      try {
        await onAcceptInvite(invite);
      } finally {
        setAcceptingId(null);
      }
    },
    [onAcceptInvite],
  );

  return (
    <div className="space-y-4">
      {invites.map((invite) => {
        const isAccepting = acceptingId === invite.id;

        const inviterDisplayName = invite.inviter.name ?? invite.inviter.email;
        return (
          <Card
            key={invite.id}
            className="transition-all duration-200 hover:shadow-lg min-w-[450px] py-3"
          >
            <CardContent className="px-4">
              <div className="flex items-center justify-between gap-6">
                <div className="flex gap-4 flex-1 items-center">
                  <Avatar className="h-12 w-12 border border-border-neutral">
                    <AvatarFallback className="dark:bg-bg-info bg-bg-info-subtle text-text-info-subtle dark:text-text-info-inverted font-semibold">
                      <BusinessCenter className="h-6 w-6 text-text-info-bold dark:text-text-info-inverted" />
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex flex-col flex-1 min-w-0 gap-1 justify-center">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-text-neutral-primary">
                        {invite.organization.displayName}
                      </h3>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-text-neutral-secondary">
                      <div className="flex items-center gap-1">
                        Role:
                        <Badge variant="outline" className="text-xs capitalize">
                          {invite.role}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Email className="h-4 w-4 mr-1" />
                        <Trans
                          i18nKey="invites.accept.invitedBy"
                          values={{
                            name: inviterDisplayName,
                          }}
                          components={{ b: <b /> }}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Schedule className="h-4 w-4" />
                        {formatDistanceToNow(new Date(invite.createdAt), {
                          addSuffix: true,
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => handleAccept(invite)}
                  disabled={isAcceptingInvite || isAccepting}
                >
                  {isAccepting
                    ? t(($) => $.invites.accept.accepting)
                    : t(($) => $.invites.accept.acceptInvite)}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {isEmpty && (
        <div className="flex h-full w-full items-center justify-center p-6">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-bg-success-subtle dark:bg-bg-success-bold mb-4">
                <CheckCircle className="h-8 w-8 text-text-success dark:text-text-success/70" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold tracking-tight">
                  {t(($) => $.invites.accept.allAccepted)}
                </h2>
                <p className="text-text-neutral-secondary">
                  {t(($) => $.invites.accept.allAcceptedDescription)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {onSkip && !isEmpty && (
        <div className="flex justify-center pt-4">
          <Button variant="ghost" onClick={onSkip}>
            {skipButtonText ?? t(($) => $.common.skipForNow)}
          </Button>
        </div>
      )}
    </div>
  );
}
